import React, { useState, useContext, useEffect } from 'react';
import {Route, useHistory, useRouteMatch} from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { useMediaQuery } from 'react-responsive'

import Markup from '../markup';

import Sidebar from './Sidebar';
import Builder from './Builder';

import Design from './Screens/Design';
import Logos from './Screens/Logos';
import Name from './Screens/Name';
import Color from './Screens/Color';
import Info from './Screens/Info';
import Flag from './Screens/components/Tabs/LogosFlag';
import MobileSave from './Screens/MobileSave';

import Tooltip from './Global/Tooltip';
import ModalCompleteMembers from './Global/ModalCompleteMembers';

import Share from './Global/Share';
import Burger from './Global/Burger';
import { device } from '../const/device';
import withBuilder from '../builder/hooks/withBuilder';
import BuilderContext from '../builder/hooks/context';
import {SaveIcon} from "./Screens/components/SaveButtons";
import Preloader from "./Global/Preloader";
import CartProgress from "./Global/CartProgress";
import { trackGoToAccount, trackGoToCart, trackReturnToStart } from '../utils/Analytics';
import { useTimeTracking } from '../utils/TimeTrackingContext';

const routes = {
  '/design/:tabIndex?': Design,
  '/logos/:tabIndex?': Logos,
  '/name/:tabIndex?': Name,
  '/color/:type/:id': Color,
  '/logos/:tabIndex/flags/:side': Flag,
  '/submit/:tabIndex?':MobileSave
};

const BaseLayout = ({ setManifest, setShareData }) => {
  const history = useHistory();
  const match = useRouteMatch();
  const [shareWindow, changeShareWindow] = useState(false);
  const [infoWindow, changeInfoWindow] = useState(false);
  const [cartCount, setCartCount] = useState(null);
  const [previousCartCount, setPreviousCartCount] = useState("0");

  const { builder } = useContext(BuilderContext);
  const { logTimeSpent } = useTimeTracking();
  const tabletS = useMediaQuery({ query: `(${device.tabletS})` });
  const tablet = useMediaQuery({ query: `(${device.tablet})` });
  const stage = match?.path?.split('/')[1] || '';

  useEffect(() => {
    return () => setShareData(null)
  },[])

  const showInfo = () => {
    if(shareWindow) changeShareWindow(false);
    changeInfoWindow(true);
  }

  const onSharedChange = (state:boolean) => {
    changeShareWindow(state);
    if(infoWindow)  changeInfoWindow(false);
  }

  const onSaveBuildChange = () => {
    if(shareWindow) changeShareWindow(false);
    if(infoWindow)  changeInfoWindow(false);
  }

  const onCartClick = () => {
    window['builderOnCart'] && window['builderOnCart']();
    trackGoToCart('icon');
    logTimeSpent();
  }

  const onUserAccountClick = () => {
    window['builderOnUserAccount'] && window['builderOnUserAccount']();
    trackGoToAccount();
    logTimeSpent();
  }

  const isVisibleTab = (path) => {
    //if we select flags
    //show flags tab is width > 1024 and hide other tab or
    // hide flags tab if size < 1024 and show pop up with flags
   return history.location.pathname.includes('/logos/1/flags') &&
    (path === '/logos/:tabIndex?' && !tablet || path === '/logos/:tabIndex/flags/:side' && tablet);
  }

  const updateCartCount = async () => {
    const cartItemsCount = window['cartItemsCount'] && await window['cartItemsCount']();
    
    if (cartItemsCount === "0" && localStorage.getItem('previousCartCount')) {
      setCartCount(localStorage.getItem('previousCartCount'));
    } else {
      setCartCount(cartItemsCount);
    }
    
    setPreviousCartCount(cartItemsCount);
    localStorage.setItem('previousCartCount', cartItemsCount);
  }

  useEffect(() => {
    const savedCartCount = localStorage.getItem('previousCartCount');
    if (savedCartCount) {
      setPreviousCartCount(savedCartCount);
    }

    updateCartCount();
  }, [builder.cnCart]);

  // Listen for cart:updated events dispatched by useExporter after add-to-cart
  useEffect(() => {
    const handleCartUpdate = () => updateCartCount();
    window.addEventListener('cart:updated', handleCartUpdate);
    return () => window.removeEventListener('cart:updated', handleCartUpdate);
  }, []);

  return (
    <Markup.BaseLayout.BaseContainer >
      <Markup.BaseLayout.Header>
        <Markup.Global.Logo
          themeColor={builder.membersData.colorTheme}
          onClick={() => {
            setManifest(null);
            history.push('/');
            window.history.replaceState({}, null, window.location.origin + window.location.pathname)
            trackReturnToStart(stage === 'name' ? 'specs' : stage);
          }}
        />

        <Burger changeShareWindow={onSharedChange} onInfoChange={showInfo} onSaveBuildChange={onSaveBuildChange}/>
        <Markup.BaseLayout.HeaderIconContainer>
          <Markup.Global.Link.LinkUserIcon onClick={onUserAccountClick} className="js-user-icon" invert={true} themeColor={builder.membersData.colorTheme} />
          <Markup.Global.Link.LinkCartIcon onClick={onCartClick} className="js-cart-icon" invert={true} themeColor={builder.membersData.colorTheme} count={cartCount !== null ? cartCount : previousCartCount}/>
        </Markup.BaseLayout.HeaderIconContainer>
      </Markup.BaseLayout.Header>

        <ModalCompleteMembers display={builder.isShowCompMessage} />

        {builder.animationState && <Markup.BaseLayout.NotActiveContent></Markup.BaseLayout.NotActiveContent>}
        {builder.saveBuilderState && <Preloader/>}
        <CartProgress />

        <Markup.BaseLayout.ContentContainer>
          <Sidebar onlyState={tabletS} />
          <Builder /> 

          {tablet && shareWindow &&
            <>
              <Markup.BaseLayout.ShareContainer>
                <Markup.BaseLayout.ShareCloseBtnContainer>
                  <Markup.Global.Button.ButtonIconClose onClick={() => onSharedChange(false)} />
                </Markup.BaseLayout.ShareCloseBtnContainer>
              <Share/>
            </Markup.BaseLayout.ShareContainer>
            {shareWindow && <Markup.Global.Share.Layout onClick={() => onSharedChange(false)}/>}
            </>
          }

          {tablet && infoWindow ? 
            <Markup.BaseLayout.InfoContainer>
              <Info changeInfoWindow={changeInfoWindow}/>
            </Markup.BaseLayout.InfoContainer>
            :      
              Object.keys(routes)
                .map(path => {
                  const Component = routes[path];
                  return (
                    <Route key={path} path={path}>
                      <CSSTransition
                        in={path.includes(match.url)}
                        timeout={1300}
                        classNames="screen"
                      >
                        <Markup.BaseLayout.ScreenContainer
                          style={{ display: isVisibleTab(path) ? 'none' : '' }}
                        >
                         {(builder?.isReady && !infoWindow) && <Component />}
                         {(builder?.isReady && infoWindow) && <Info changeInfoWindow={changeInfoWindow}/>}
                        </Markup.BaseLayout.ScreenContainer>
                      </CSSTransition>
                    </Route>
                  )
                })
              
            }
        </Markup.BaseLayout.ContentContainer>

      <Markup.BaseLayout.Footer>

        <Markup.BaseLayout.FooterIconContainer>
          <Markup.Global.Link.LinkInfoIcon href="#" onClick={showInfo} themeColor={builder.membersData.colorTheme}/>
          <Tooltip Icon={Markup.Global.Link.LinkShareIcon} width={360} offset={-49} borderRadius={4} top children={<Share />} />
          <SaveIcon/>
        </Markup.BaseLayout.FooterIconContainer>

      </Markup.BaseLayout.Footer>

    </Markup.BaseLayout.BaseContainer>
  );
}

export default React.memo(withBuilder(BaseLayout));
